/**
 * PSMF client-side persistence.
 * TODO: Back-end persistence — add /api/psmf routes. Until then, PSMFs
 * live in localStorage keyed by `psmf:<tenantId>:<id>` with an index at
 * `psmf:<tenantId>:index` listing PSMF ids.
 */

import type { Psmf } from './types';

const INDEX_KEY = (tenantId: string) => `psmf:${tenantId}:index`;
const DATA_KEY = (tenantId: string, id: string) => `psmf:${tenantId}:${id}`;

export function listPsmfs(tenantId: string): Psmf[] {
  const raw = safeRead(INDEX_KEY(tenantId));
  if (!raw) return [];
  let ids: string[];
  try {
    ids = JSON.parse(raw);
  } catch {
    return [];
  }
  const out: Psmf[] = [];
  for (const id of ids) {
    const v = loadPsmf(tenantId, id);
    if (v) out.push(v);
  }
  out.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return out;
}

export function loadPsmf(tenantId: string, id: string): Psmf | null {
  const raw = safeRead(DATA_KEY(tenantId, id));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Psmf;
  } catch {
    return null;
  }
}

export function savePsmf(psmf: Psmf): void {
  const { tenantId, id } = psmf;
  try {
    localStorage.setItem(DATA_KEY(tenantId, id), JSON.stringify(psmf));
    const idx = listPsmfIds(tenantId);
    if (!idx.includes(id)) {
      idx.push(id);
      localStorage.setItem(INDEX_KEY(tenantId), JSON.stringify(idx));
    }
  } catch {
    // localStorage may be unavailable (SSR / private mode).
  }
}

export function deletePsmf(tenantId: string, id: string): void {
  try {
    localStorage.removeItem(DATA_KEY(tenantId, id));
    const idx = listPsmfIds(tenantId).filter((x) => x !== id);
    localStorage.setItem(INDEX_KEY(tenantId), JSON.stringify(idx));
  } catch {
    // ignore
  }
}

export function newPsmfId(): string {
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function listPsmfIds(tenantId: string): string[] {
  const raw = safeRead(INDEX_KEY(tenantId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
